/**
 * TSCheckTable.tsxのソースコード量が多くなっていたので
 * データ処理に関して別ファイルに分割した
 *
 */

import {
  deleteFirestoreDoc,
  getFirestoreDataWhereArrayAny,
  getFirestoreDocData,
  writeFirestoreDoc,
} from "../../../apis/firestoreAction";
import { v4 as uuidv4 } from "uuid";
import { firestore, getFirebaseUserInfo } from "../../../firebase";
import { zonedTimeToUtc } from "date-fns-tz";
import { userLogging } from "../../../apis/userLog";

// デフォルトで「-」入力にする項目の設定
// Phase2では「-」項目はマスタデータから削除されているため、空配列
const DEFAULT_MINUS_ITEMS: Array<{ big_item_id: number; small_item_id: number }> = [];

// 指定された項目がデフォルトで「-」入力になる項目かどうかを判定する関数
export const isDefaultMinusItem = (big_item_id: number, small_item_id: number): boolean => {
  return DEFAULT_MINUS_ITEMS.some(
    item => item.big_item_id === big_item_id && item.small_item_id === small_item_id
  );
};

// 店舗衛生点検表個別項目の型
export interface TS_CHECK_DATA {
  tenpo_code: string;
  id: string;
  last_time_id: string;
  zone_code: string;
  do_code: string;
  approve_day: string;
  complete_date: string;
  last_approve_day: string;
  update_user: string;
  approve_weather: string;
  approve_hour:string;
  septic_tank_value: string;
  inspection_value: string;
  inspection_day: string;
  last_inspection_day: string;
  confirmation_value: string;
  confirmation_day: string;
  last_confirmation_day: string;
  // 基本項目
  base_value: Array<TS_CHECK_BIG_RECORD>;
  // 追加項目
  add_value: Array<TS_CHECK_ADD_BIG_RECORD>;
}

// 店舗衛生点検表の大項目単位の値の型
export interface TS_CHECK_BIG_RECORD {
  big_title: string;
  big_item_id: number;
  rows: Array<TS_CHECK_RECORD>;
}

// 店舗衛生点検表の追加項目の大項目単位の値の型(拡張)
export interface TS_CHECK_ADD_BIG_RECORD extends TS_CHECK_BIG_RECORD {
  maximum_small_item_id: number;
  rows: Array<TS_CHECK_ADD_RECORD>;
}

// 店舗衛生点検表の小項目単位の値の型
export interface TS_CHECK_RECORD {
  key: string; // 「2-3」のような「{大項目}-{小項目}」の形式
  big_item_id: number;
  small_title: string;
  small_item_id: number;
  score: string;
  button_value: string;
  last_score: string;
  last_button_value: string;
  photo_filepath_gcs: string[];
  manual: string[];
  user_input_comment: string;
  comment_filepath_gcs: string[];
  comment_handwrite_flag: string;
  user_radio_comment: string;
  user_radio_target: string;
  reason_comment_list: SELECTMAP[];
  reason_target_list: SELECTMAP[];
  user_ng_list: any[];
  last_user_input_comment?: string;
  last_photo_filepath_gcs?: string[];
  last_comment_filepath_gcs?: string[];
  last_comment_handwrite_flag?: string;
  item_update_day?: string;
}
// 店舗衛生点検表の詳細項目の値の型
export interface REASONMAP {
  value: string;
  comment_list: SELECTMAP[];
}
// 店舗衛生点検表の選択項目の値の型
export interface SELECTMAP {
  name: string;
  value: string;
}
// 店舗衛生点検表の選択項目の値の型
export interface USERNGMAP {
  user_radio_comment: string;
  user_radio_target: string;
}
// 店舗衛生点検表の追加項目の大項目単位の値の型(拡張)
export interface TS_CHECK_ADD_RECORD extends TS_CHECK_RECORD {
  uuid: string;
}

// 店舗衛生点検表のテンプレートの型
export interface TS_CHECK_ITEM_TEMPLATE {
  id: string;
  doc_id: string;
  template_name: string;
  conditions: string[];
  big_item_id: number;
  big_item_name: string;
  small_items: Array<TEMPLATE_ITEM>;
  info: {
    create_date: any;
    create_user: string;
    update_date: any;
    update_user: string;
  };
}
// テンプレート内の追加項目の型
export interface TEMPLATE_ITEM {
  id: number;
  name: string;
  uuid: string;
  photo_filepath_gcs: string[];
  user_input_comment: string;
  comment_filepath_gcs: string[];
  comment_handwrite_flag: string;
}

//Todo 追加項目
const addItemBigRecordId: string = "008";
const addItemBigRecordDmId: string = "008_dm";
const addItemBigRecordZmId: string = "008_zm";

// Firestoreより3レコード取得して結果をマージし、UIに表示するデータを作る
export const getTSCheckData = async (
  tenpoCode: string,
  docId: string,
  lastDocId: string,
  fixedFlag: boolean,
  userPrivilege: string,
  ofcFlg: boolean
) => {
  // 1:3レコード取得する
  // 大項目番号1～7の共通大項目の場合
  const baseItemValues = await getFirestoreDocData(
    "ts_check_items",
    "000000_basedata_phase2"
  );
  const addItemValues = await getFirestoreDocData(
    "tenpo_check_items",
    tenpoCode
  );
  let retDocId = null;
  if (docId) {
    retDocId = await getFirestoreDocData("ts_check_records_phase2", docId);
    if (!retDocId) {
      // firestoreからデータが取得できないとき（初回でデータがない場合）必要な情報を以下作成
      const baseItemValue: any = baseItemValues;
      let smallValueArray: Array<TS_CHECK_RECORD> = [];
      let bigValueArray: Array<TS_CHECK_BIG_RECORD> = [];
      Object.keys(baseItemValue.attribution)
        .sort()
        .forEach(function (key) {
          const bigValue: TS_CHECK_BIG_RECORD = {
            big_title: baseItemValue.attribution[key].big_item_name,
            big_item_id: baseItemValue.attribution[key].big_item_id,
            // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
            rows: makeBigRecordArray(
              baseItemValue.attribution[key].big_item_id,
              baseItemValue.attribution[key].small_items,
              smallValueArray,
              false,
              false
            ),
          };
          bigValueArray.push(bigValue);
        });
      let records: any = {};
      for (const bigRecord of bigValueArray) {
        for (const smallRecord of bigRecord.rows) {
          const keyValue: string = "records-" + smallRecord.key;
          records[keyValue] = smallRecord;
        }
      }
      let attribution: any = {
        approve_day: "",
        last_approve_day: "",
        septic_tank_value: "",
        inspection_value: "",
        inspection_day: "",
        last_inspection_day: "",
        confirmation_value: "",
        confirmation_day: "",
        last_confirmation_day: "",
      };
      let update_date: any = {
        update_user: "",
      };
      retDocId = {
        records: records,
        attribution: attribution,
        update_date: update_date,
      };
    }
  }
  const docIdData: any = retDocId;
  let lastMonth: any = null;
  if (docIdData && docIdData.attribution && docIdData.attribution.last_time_id) {
    lastMonth = await getJsonlastMonths(docIdData.attribution.last_time_id);
  }
  const lastMonthRecords = lastMonth || {};
  return editData(
    baseItemValues,
    addItemValues,
    docIdData,
    tenpoCode,
    docId,
    lastDocId,
    fixedFlag,
    lastMonthRecords,
    userPrivilege || "",
    ofcFlg
  );
};

//前回の追加項目のuuidを検索して返す
function getLastAddItem(lastRecords: any, currentRecords: any) {
  let lastMonthRecord: any = null;
  const keys = lastRecords ? sortObject(lastRecords) : [];
  Object.keys(keys)
  .sort()
  .forEach(function (key: any) {
    //追加項目のuuidを検索
    if (keys[key]["uuid"] === currentRecords["uuid"]) {
      lastMonthRecord = keys[key];
    }
  });
  return lastMonthRecord;
}

const editData = (
  baseItemValues: any,
  addItemValues: any,
  docIdData: any,
  tenpoCode: string,
  docId: string,
  lastDocId: string,
  fixedFlag: boolean,
  lastMonthRecords: any,
  userPrivilege: string,
  ofcFlg: boolean
): TS_CHECK_DATA => {
  let completeFlg = false;
  if (docIdData && docIdData.attribution.complete_date) {
    completeFlg = true;
  }
  const data: TS_CHECK_DATA = {
    tenpo_code: tenpoCode,
    id: docId,
    last_time_id: lastDocId,
    zone_code: "",
    do_code: "",
    approve_day: docIdData ? docIdData.attribution.approve_day : "",
    complete_date: docIdData ? docIdData.attribution.complete_date : "",
    last_approve_day: docIdData ? docIdData.attribution.last_approve_day : "",
    update_user: docIdData ? docIdData.update_date.update_user : "",
    approve_weather: docIdData ? docIdData.attribution.approve_weather : "",
    approve_hour:docIdData ? docIdData.attribution.approve_hour : "",
    septic_tank_value: docIdData ? docIdData.attribution.septic_tank_value ? docIdData.attribution.septic_tank_value : "" : "",
    inspection_value: docIdData ? docIdData.attribution.inspection_value ? docIdData.attribution.inspection_value : "" : "",
    inspection_day: docIdData ? docIdData.attribution.inspection_day : "",
    last_inspection_day: docIdData ? docIdData.attribution.last_inspection_day : "",
    confirmation_value: docIdData ? docIdData.attribution.confirmation_value ? docIdData.attribution.confirmation_value : "" : "",
    confirmation_day: docIdData ? docIdData.attribution.confirmation_day : "",
    last_confirmation_day: docIdData ? docIdData.attribution.last_confirmation_day ? docIdData.attribution.last_confirmation_day : "" : "",
    base_value: [],
    add_value: [],
  };
  let smallValueArray: Array<TS_CHECK_RECORD> = [];
  let smallAddValueArray: Array<TS_CHECK_ADD_RECORD> = [];
  const keys = docIdData ? sortObject(docIdData["records"]) : [];
  Object.keys(keys)
    .sort()
    .forEach(function (key: any) {
      let recordKey = "records-" + keys[key]["key"];
      let lastMonthScore: string = "";
      let lastMonthButton: string = "";
      let lastUserInputComment: string = "";
      let lastPhotoFilepathGcs: string[] = [];
      let lastCommentFilepathGcs: string[] = [];
      let lastCommentHandwriteFlag: string = "";
      if (lastMonthRecords[recordKey]) {
        lastMonthScore = lastMonthRecords[recordKey]["score"];
        lastMonthButton = lastMonthRecords[recordKey]["button_value"];
        lastUserInputComment = lastMonthRecords[recordKey]["user_input_comment"];
        lastPhotoFilepathGcs = lastMonthRecords[recordKey]["photo_filepath_gcs"];
        lastCommentFilepathGcs = lastMonthRecords[recordKey]["comment_filepath_gcs"];
        lastCommentHandwriteFlag = lastMonthRecords[recordKey]["comment_handwrite_flag"];
      } else {
        // lastMonthRecordsが空の場合、Firestoreから読み込んだ元のデータを使用
        lastMonthScore = keys[key]["last_score"] || "";
        lastMonthButton = keys[key]["last_button_value"] || "";
        lastUserInputComment = keys[key]["last_user_input_comment"] || "";
        lastPhotoFilepathGcs = keys[key]["last_photo_filepath_gcs"] || [];
        lastCommentFilepathGcs = keys[key]["last_comment_filepath_gcs"] || [];
        lastCommentHandwriteFlag = keys[key]["last_comment_handwrite_flag"] || "";
      }
      if (keys[key]["big_item_id"] !== 9) {
        // デフォルトで「-」入力にする項目かチェック（参照モードでは強制しない）
        const shouldBeMinusItem = !fixedFlag && isDefaultMinusItem(
          keys[key]["big_item_id"],
          keys[key]["small_item_id"]
        );
        const smallValue: TS_CHECK_RECORD = {
          key: keys[key]["key"],
          big_item_id: keys[key]["big_item_id"],
          small_title: keys[key]["small_title"],
          small_item_id: keys[key]["small_item_id"],
          score: shouldBeMinusItem ? "-1" : keys[key]["score"],
          button_value: shouldBeMinusItem ? "3" : keys[key]["button_value"],
          last_score: lastMonthScore,
          last_button_value: lastMonthButton,
          photo_filepath_gcs: keys[key]["photo_filepath_gcs"],
          manual: [],
          user_input_comment: keys[key]["user_input_comment"],
          comment_filepath_gcs: keys[key]["comment_filepath_gcs"] || [],
          comment_handwrite_flag: keys[key]["comment_handwrite_flag"] || "",
          user_radio_comment: keys[key]["user_radio_comment"],
          user_radio_target: keys[key]["user_radio_target"] || "",
          reason_comment_list: keys[key]["reason_comment_list"] || [],
          reason_target_list: keys[key]["reason_target_list"] || [],
          user_ng_list: keys[key]["user_ng_list"] || [],
          last_user_input_comment: lastUserInputComment,
          last_photo_filepath_gcs: lastPhotoFilepathGcs,
          last_comment_filepath_gcs: lastCommentFilepathGcs || [],
          last_comment_handwrite_flag: lastCommentHandwriteFlag || "",
        };
        smallValueArray.push(smallValue);
      } else {
        // 前回の追加項目のuuidを検索して返す
        let lastMonthRecord = getLastAddItem(lastMonthRecords, keys[key]);
        //追加項目のuuidが合致した場合、同じbigItem-SmallItemとしてデータを保存
        if (lastMonthRecord !== null) {
          lastMonthScore = lastMonthRecord["score"];
          lastMonthButton = lastMonthRecord["button_value"];
          lastUserInputComment = lastMonthRecord["user_input_comment"];
          lastPhotoFilepathGcs = lastMonthRecord["photo_filepath_gcs"];
          lastCommentFilepathGcs = lastMonthRecord["comment_filepath_gcs"];
          lastCommentHandwriteFlag = lastMonthRecord["comment_handwrite_flag"];
        }
        //uuidが合致しなかった場合、Firestoreから読み込んだ元のデータを使用
        else {
          lastMonthScore = keys[key]["last_score"] || "";
          lastMonthButton = keys[key]["last_button_value"] || "";
          lastUserInputComment = keys[key]["last_user_input_comment"] || "";
          lastPhotoFilepathGcs = keys[key]["last_photo_filepath_gcs"] || [];
          lastCommentFilepathGcs = keys[key]["last_comment_filepath_gcs"] || [];
          lastCommentHandwriteFlag = keys[key]["last_comment_handwrite_flag"] || "";
        }
        // デフォルトで「-」入力にする項目かチェック（参照モードでは強制しない）
        const shouldBeMinusItem = !fixedFlag && isDefaultMinusItem(
          keys[key]["big_item_id"],
          keys[key]["small_item_id"]
        );
        const smallValue: TS_CHECK_ADD_RECORD = {
          key: keys[key]["key"],
          big_item_id: keys[key]["big_item_id"],
          small_title: keys[key]["small_title"],
          small_item_id: keys[key]["small_item_id"],
          score: shouldBeMinusItem ? "-1" : keys[key]["score"],
          button_value: shouldBeMinusItem ? "3" : keys[key]["button_value"],
          last_score: lastMonthScore,
          last_button_value: lastMonthButton,
          photo_filepath_gcs: keys[key]["photo_filepath_gcs"],
          manual: [],
          user_input_comment: keys[key]["user_input_comment"],
          comment_filepath_gcs: keys[key]["comment_filepath_gcs"] || [],
          comment_handwrite_flag: keys[key]["comment_handwrite_flag"] || "",
          user_radio_comment: keys[key]["user_radio_comment"],
          user_radio_target: keys[key]["user_radio_target"] || "",
          reason_comment_list: keys[key]["reason_comment_list"] || [],
          reason_target_list: keys[key]["reason_target_list"] || [],
          uuid: keys[key]["uuid"],
          user_ng_list: keys[key]["user_ng_list"] || [],
          last_user_input_comment: lastUserInputComment,
          last_photo_filepath_gcs: lastPhotoFilepathGcs,
          last_comment_filepath_gcs: lastCommentFilepathGcs || [],
          last_comment_handwrite_flag: lastCommentHandwriteFlag || "",
        };
        smallAddValueArray.push(smallValue);
      }
    });

  let bigValueArray: Array<TS_CHECK_BIG_RECORD> = [];
  let bigValueAddArray: Array<TS_CHECK_ADD_BIG_RECORD> = [];
  // 大項目(共通大項目が10を超えるとソートが崩れるので注意!)
  Object.keys(baseItemValues.attribution)
    .sort()
    .forEach(function (key) {
      const bigValue: TS_CHECK_BIG_RECORD = {
        big_title: baseItemValues.attribution[key].big_item_name,
        big_item_id: baseItemValues.attribution[key].big_item_id,
        // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
        rows: makeBigRecordArray(
          baseItemValues.attribution[key].big_item_id,
          baseItemValues.attribution[key].small_items,
          smallValueArray,
          completeFlg,
          fixedFlag
        ),
      };
      bigValueArray.push(bigValue);
    });
  // 追加項目
  const bigAddValue = (): TS_CHECK_ADD_BIG_RECORD => {
    let addItemUserPrivilege = userPrivilege;
    if (fixedFlag) {
      addItemUserPrivilege = docIdData
        ? docIdData.attribution.user_privilege
        : "";
    }
    let itemBigRecordId = addItemBigRecordId;
    if (!ofcFlg) {
      if (addItemUserPrivilege === "dm") {
        itemBigRecordId = addItemBigRecordDmId;
      } else if (addItemUserPrivilege === "zm") {
        itemBigRecordId = addItemBigRecordZmId;
      }
    }
    if (fixedFlag) {
      return {
        big_title: addItemValues.attribution[itemBigRecordId]["big_item_name"],
        big_item_id: addItemValues.attribution[itemBigRecordId]["big_item_id"],
        maximum_small_item_id: getAddValuesMaxId(
          addItemValues.attribution[itemBigRecordId]["small_items"]
        ),
        // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
        rows: smallAddValueArray,
      };
    } else if (
      addItemValues &&
      addItemValues.attribution[itemBigRecordId] &&
      addItemValues.attribution[itemBigRecordId]["big_item_id"]
    ) {
      return {
        big_title:
          addItemValues.attribution[itemBigRecordId]["big_item_name"],
        big_item_id:
          addItemValues.attribution[itemBigRecordId]["big_item_id"],
        maximum_small_item_id: getAddValuesMaxId(
          addItemValues.attribution[itemBigRecordId]["small_items"]
        ),
        // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
        rows: linkItemArray(smallAddValueArray, itemBigRecordId, addItemValues),
      };
    } else {
      const temp: TS_CHECK_ADD_BIG_RECORD = {
        big_title: "商品・売場・地区政策等",
        big_item_id: 9,
        maximum_small_item_id: 0,
        rows: [],
      };
      const addItem = {
        big_item_name: "商品・売場・地区政策等",
        big_item_id: 8,
        maximum_small_item_id: 0,
        small_items: linkItemArray([], itemBigRecordId, addItemValues),
      };
      const objects: any = {};
      let recordItem = {};
      let recordItemDm = {};
      let recordItemZm = {};
      if (addItemValues) {
        recordItem = addItemValues["attribution"][addItemBigRecordId] || {};
        recordItemDm = addItemValues["attribution"][addItemBigRecordDmId] || {};
        recordItemZm = addItemValues["attribution"][addItemBigRecordZmId] || {};
      }
      if (!ofcFlg && userPrivilege === "dm") {
        objects["attribution"] = {
          "008": recordItem,
          "008_dm": addItem,
          "008_zm": recordItemZm,
        };
      } else if (userPrivilege === "zm") {
        objects["attribution"] = {
          "008": recordItem,
          "008_dm": recordItemDm,
          "008_zm": addItem,
        };
      } else {
        objects["attribution"] = {
          "008": addItem,
          "008_dm": recordItemDm,
          "008_zm": recordItemZm,
        };
      }
      if (!fixedFlag) {
        userLogging(
          "店舗衛生点検表",
          "新規作成#tenpo_check_itemsテーブル追加",
          JSON.stringify(objects)
        );
        writeFirestoreDoc("tenpo_check_items", tenpoCode, objects);
      }
      return temp;
    }
  };
  bigValueAddArray.push(bigAddValue());
  data["base_value"] = bigValueArray;
  data["add_value"] = bigValueAddArray;
  return data;
};

// 追加項目の項目連携
const linkItemArray = (
  smallAddValueArray: Array<TS_CHECK_ADD_RECORD>,
  itemBigRecordId: string,
  addItemValues: any
) => {
  if (!addItemValues || !addItemValues["attribution"][itemBigRecordId]) {
    return [];
  }
  let resultArray: Array<TS_CHECK_ADD_RECORD> = [];
  let smallItems = addItemValues["attribution"][itemBigRecordId]["small_items"];
  if (smallItems) {
    for (let i = 0; i < smallItems.length; i++) {
      let recordFlg = false;
      let keyNum = Number(i) + 1;
      for (const val of smallAddValueArray) {
        if (smallItems[i].uuid === val.uuid) {
          recordFlg = true;
          val.key = "9-" + keyNum;
          val.small_title = smallItems[i].name;
          val.small_item_id = keyNum;
          resultArray.push(val);
          break;
        }
      }
      if (!recordFlg) {
        const smallValue: TS_CHECK_ADD_RECORD = {
          key: "9-" + keyNum,
          big_item_id: 9,
          small_title: smallItems[i].name,
          small_item_id: keyNum,
          score: "",
          button_value: "",
          last_score: "",
          last_button_value: "",
          photo_filepath_gcs: [],
          manual: [],
          user_input_comment: "",
          comment_filepath_gcs: [],
          comment_handwrite_flag: "",
          user_radio_comment: "",
          user_radio_target: "",
          reason_comment_list: [],
          reason_target_list: [],
          uuid: smallItems[i]["uuid"],
          user_ng_list: [],
        };
        resultArray.push(smallValue);
      }
    }
  }
  return resultArray;
};

// レコードを作る関数
const makeBigRecordArray = (
  bigItemId: string,
  smallItems: any,
  smallValueArray: Array<TS_CHECK_RECORD>,
  completeFlg: boolean,
  fixedFlag: boolean = false
): Array<TS_CHECK_RECORD> => {
  const searchRecord = (
    bigItemId: string,
    smallItemId: string,
    smallItemTitle: string,
    manual?: any[],
    reason_comment_list?: any[],
    reason_target_list?: any[],
    item_update_day?: string,
  ) => {
    let key = bigItemId + "-" + smallItemId;
    if (bigItemId === "9") {
      key = "8-" + smallItemId;
    }

    for (const row of smallValueArray) {
      if (row["key"] === key) {
        row["small_title"] = smallItemTitle;
        if (manual) {
          row["manual"] = manual;
        }
        if (reason_comment_list) {
          row["reason_comment_list"] = reason_comment_list;
        }
        if (reason_target_list) {
          row["reason_target_list"] = reason_target_list;
        }
        if (item_update_day) {
          row["item_update_day"] = item_update_day;
        }
        // デフォルトで「-」入力にする項目の場合、強制的に設定（参照モードでは強制しない）
        if (!fixedFlag && isDefaultMinusItem(Number(bigItemId), Number(smallItemId))) {
          row["score"] = "-1";
          row["button_value"] = "3";
        }
        return row;
      }
    }
    const tmp: TS_CHECK_RECORD = {
      key,
      big_item_id: Number(bigItemId),
      small_title: smallItemTitle,
      small_item_id: Number(smallItemId),
      score: isDefaultMinusItem(Number(bigItemId), Number(smallItemId)) ? "-1" : "0",
      button_value: isDefaultMinusItem(Number(bigItemId), Number(smallItemId)) ? "3" : "0",
      last_score: "0",
      last_button_value: "0",
      photo_filepath_gcs: [],
      manual: [],
      user_input_comment: "",
      comment_filepath_gcs: [],
      comment_handwrite_flag: "",
      user_radio_comment: "",
      user_radio_target: "",
      reason_comment_list: [],
      reason_target_list: [],
      user_ng_list: [],
    };
    return tmp;
  };
  let array: Array<TS_CHECK_RECORD> = [];
  for (const row of smallItems) {
    const val = searchRecord(
      bigItemId,
      row.id,
      row.name,
      row.manual,
      row.reason_comment_list,
      row.reason_target_list,
      row.update_day,
    );
    if (val !== undefined) {
      array.push(val);
    }
  }
  return array;
};

// 追加項目の小項目の最大の値を取得する
const getAddValuesMaxId = (smallItems: any): number => {
  let maxNumber = 0;
  if (smallItems) {
    for (const row of smallItems) {
      if (row.id > maxNumber) maxNumber = row.id;
    }
  }
  return maxNumber;
};

// 配列の順番を整えるため、ここだけ連想配列キーを「xx-yy(例:02-11)」のような形にする
const sortObject = (objects: any) => {
  let newObj: any = {};
  Object.keys(objects).forEach(function (key) {
    const tempKey =
      ("00" + objects[key]["big_item_id"]).slice(-2) +
      ("00" + objects[key]["small_item_id"]).slice(-2);
    newObj[tempKey] = objects[key];
  });
  return newObj;
};

/**
 * 店舗衛生点検表の変更項目を差分更新し、最終的にReactHooksに保存する
 * @param tsCheckData 店舗衛生点検表(基本項目)の全てのデータ
 * @returns
 */
export const updateTSCheck = (
  tsCheckData: TS_CHECK_BIG_RECORD[],
  newData: TS_CHECK_RECORD
): TS_CHECK_BIG_RECORD[] => {
  const newTSCheckData: TS_CHECK_BIG_RECORD[] = [];
  for (const bigRecord of tsCheckData) {
    let newArray: TS_CHECK_RECORD[] = [];
    for (const record of bigRecord.rows) {
      if (record.key === newData.key) {
        newArray.push(newData);
      } else {
        newArray.push(record);
      }
    }
    bigRecord.rows = newArray;
    newTSCheckData.push(bigRecord);
  }
  return newTSCheckData;
};

/**
 * 店舗衛生点検表の変更項目を差分更新し、最終的にReactHooksに保存する
 * @param tsCheckData 店舗衛生点検表(追加項目)の全てのデータ
 * @param newNumber -1の場合は新規追加して最後尾にpushする
 * @returns
 */
export const updateTSCheckAddValue = (
  tsCheckData: TS_CHECK_ADD_BIG_RECORD[],
  newData: TS_CHECK_ADD_RECORD,
  isNew: boolean = false
) => {
  const newTSCheckData: TS_CHECK_ADD_BIG_RECORD[] = [];
  for (const bigRecord of tsCheckData) {
    let newArray: TS_CHECK_ADD_RECORD[] = [];
    for (const addRecord of bigRecord.rows) {
      if (addRecord.key === newData.key && !isNew) {
        newArray.push(newData);
      } else {
        newArray.push(addRecord);
      }
    }
    if (isNew) {
      newArray.push(newData);
    }
    bigRecord.rows = newArray;
    newTSCheckData.push(bigRecord);
  }
  return newTSCheckData;
};

/**
 * 店舗衛生点検表の値を受け取り、Firestoreの更新を行う
 * @param basicItemList 基本項目の配列
 * @param addRowItemList 追加項目の配列
 *
 */
export const updateTSCheckFirestore = (
  rowItemList: Array<TS_CHECK_BIG_RECORD>,
  addRowItemList: Array<TS_CHECK_BIG_RECORD>,
  tsCheckId: string,
  totalScore: number,
  tsCheckLastTimeId: string,
  selectedTenpoCode: string,
  approveDay: string,
  lastApproveDay: string,
  userPrivilege: string,
  approveWeather: string,
  approveHour:string,
  septicTankValue: string,
  inspectionValue: string,
  inspectionDay: string,
  lastInspectionDay: string,
  confirmationValue: string,
  confirmationDay: string,
  lastConfirmationDay: string,
) => {
  if (
    (Array.isArray(rowItemList) && rowItemList.length === 0) ||
    tsCheckId === ""
  ) {
    // 初回読み込みで呼ばれてしまうので空であれば終了する
    return;
  }
  const objects: any = {};
  objects["attribution"] = {
    conditions: ["ALL"],
    last_time_id: tsCheckLastTimeId,
    tenpo_code: selectedTenpoCode,
    total_score: String(totalScore),
    approve_day: approveDay,
    last_approve_day: lastApproveDay,
    user_privilege: userPrivilege,
    approve_weather: approveWeather,
    approve_hour: approveHour,
    septic_tank_value: septicTankValue,
    inspection_value: inspectionValue,
    inspection_day: inspectionDay,
    last_inspection_day: lastInspectionDay,
    confirmation_value: confirmationValue,
    confirmation_day: confirmationDay,
    last_confirmation_day: lastConfirmationDay,
  };
  objects["id"] = tsCheckId;
  objects["docId"] = tsCheckId;
  objects["update_date"] = {
    update_date: firestore.FieldValue.serverTimestamp(),
    update_user: getFirebaseUserInfo().uid,
  };
  let records: any = {};
  for (const bigRecord of rowItemList) {
    for (const smallRecord of bigRecord.rows) {
      const keyValue: string = "records-" + smallRecord.key;
      records[keyValue] = smallRecord;
    }
  }
  for (const bigRecord of addRowItemList) {
    for (const smallRecord of bigRecord.rows) {
      const keyValue: string = "records-" + smallRecord.key;
      records[keyValue] = smallRecord;
    }
  }
  objects["records"] = records;
  userLogging(
    "店舗衛生点検表",
    "新規作成#ts_check_recordsテーブル更新",
    JSON.stringify(objects)
  );
  writeFirestoreDoc("ts_check_records_phase2", tsCheckId, objects);
};

// 追加ボタン押下時の新規レコード追加処理
export const createNewRecord = (
  small_title: string,
  addRowItemLArray: TS_CHECK_ADD_BIG_RECORD,
  photo_filepath_gcs: string[],
  user_input_comment: string,
  comment_filepath_gcs: string[],
  comment_handwrite_flag: string,
  uuid?: string
): TS_CHECK_ADD_RECORD => {
  const big_item_id = 9;
  let maxSmallId = 0;
  for (const row of addRowItemLArray.rows) {
    if (Number(row.key.split("-")[1]) > maxSmallId) {
      maxSmallId = Number(row.key.split("-")[1]);
    }
  }
  const new_small_item_id = maxSmallId + 1;
  const val: TS_CHECK_ADD_RECORD = {
    key: big_item_id + "-" + new_small_item_id,
    big_item_id: big_item_id,
    small_title: small_title,
    small_item_id: Number(new_small_item_id),
    score: "0",
    button_value: "0",
    last_score: "0",
    last_button_value: "0",
    photo_filepath_gcs: photo_filepath_gcs ? photo_filepath_gcs : [],
    manual: [],
    user_input_comment: user_input_comment ? user_input_comment : "",
    comment_filepath_gcs: comment_filepath_gcs ? comment_filepath_gcs : [],
    comment_handwrite_flag: comment_handwrite_flag ? comment_handwrite_flag : "",  
    user_radio_comment: "",
    user_radio_target: "",
    reason_comment_list: [],
    reason_target_list: [],
    uuid: uuidv4(),
    user_ng_list: [],
  };
  return val;
};

// 削除ボタン押下時
export const deleteAddItems = (
  deleteClickList: string[],
  addRowItemList: TS_CHECK_ADD_BIG_RECORD[]
) => {
  let resultList = [];
  for (const addRecord of addRowItemList[0].rows) {
    if (!deleteClickList.includes(addRecord.key)) {
      resultList.push(addRecord);
    }
  }
  addRowItemList[0].rows = resultList;
  return addRowItemList;
};

// ソートボタン押下時
export const sortAddItems = (
  addRowItemList: TS_CHECK_ADD_BIG_RECORD[],
  isUp: boolean,
  key: string
) => {
  // 上から順番を取得する
  let array: TS_CHECK_ADD_RECORD[] = addRowItemList[0].rows;
  let order: number = 0;
  for (const addRecord of array) {
    if (key === addRecord.key) {
      // 0始まりの順番
      break;
    }
    order++;
  }
  let nextOrderKey: string = key;
  // 前に移動
  if (isUp) {
    let x = order,
      y = order - 1;
    if (y < 0) {
      y = order;
    }
    let temp = array[order];
    const xKey = array[x].key;
    const yKey = array[y].key;
    nextOrderKey = yKey;
    array[x] = array[y];
    array[y] = temp;
    array[x].key = xKey;
    array[y].key = yKey;
  } else if (isUp === false) {
    let x = order,
      y = order + 1;
    if (array.length === y) {
      y = order;
    }
    let temp = array[order];
    const xKey = array[x].key;
    const yKey = array[y].key;
    nextOrderKey = yKey;
    array[x] = array[y];
    array[y] = temp;
    array[x].key = xKey;
    array[y].key = yKey;
  }
  addRowItemList[0].rows = array;
  return { addRowItemList, nextKey: nextOrderKey };
};

export const getTenpoFirestoreData = async (tenpoCode: string) => {
  const data = await getFirestoreDocData("existing_sej_stores", tenpoCode).then(
    (response: any) => {
      return response;
    }
  );
  return data;
};

// 追加項目のFirestoreデータ更新を行う
export const updateTSCheckAddItem = async (
  addRowItemList: TS_CHECK_ADD_BIG_RECORD[],
  tenpoCode: string,
  userPrivilege: string,
  ofcFlg: boolean
) => {
  let addItem = [];
  for (const row of addRowItemList[0].rows) {
    addItem.push({
      id: row.key.split("-")[1],
      name: row.small_title,
      uuid: row.uuid,
      offense_flg: "0",
    });
  }
  // 追加項目の取得を行う
  const addItemValues = await getFirestoreDocData(
    "tenpo_check_items",
    tenpoCode
  ).then((response: any) => {
    return response;
  });

  let itemBigRecordId = addItemBigRecordId;
  if (!ofcFlg) {
    if (userPrivilege === "dm") {
      itemBigRecordId = addItemBigRecordDmId;
    } else if (userPrivilege === "zm") {
      itemBigRecordId = addItemBigRecordZmId;
    }
  }
  // 攻め守りフラグを更新させない
  let smallItems = addItemValues["attribution"][itemBigRecordId]["small_items"];
  if (smallItems) {
    for (let item of addItem ) {
      for (let smallItem of smallItems) {
        if (item.uuid === smallItem.uuid) {
          item.offense_flg = smallItem.offense_flg || "0";
        }
      }
    }
  }
  addItemValues["attribution"][itemBigRecordId]["small_items"] = addItem;

  userLogging(
    "店舗衛生点検表",
    "新規作成#tenpo_check_itemsテーブル更新",
    JSON.stringify(addItemValues)
  );
  writeFirestoreDoc("tenpo_check_items", tenpoCode, addItemValues);
};


export function getFirstDayOfMonth(target_date: string, addMonth: number) {
  // 日付取得
  let today = zonedTimeToUtc(target_date, "Asia/Tokyo");
  let this_year = today.getFullYear();
  let this_month = today.getMonth();

  // 年月
  let firstDayOfMonth = new Date(this_year, this_month + addMonth, 1);
  let year = firstDayOfMonth.getFullYear() + "";
  let month = firstDayOfMonth.getMonth() + 1 < 10
        ? "0" + (firstDayOfMonth.getMonth() + 1)
        : firstDayOfMonth.getMonth() + 1 + "";
  //文字列を作成
  return year + month + "01";
}

export function getLastDayOfMonth(target_date: string, addMonth: number) {
  // 日付取得
  let today = zonedTimeToUtc(target_date, "Asia/Tokyo");
  let this_year = today.getFullYear();
  let this_month = today.getMonth();

  // 年月
  let lastDayOfMonth = new Date(this_year, this_month + addMonth, 0);
  let year = lastDayOfMonth.getFullYear() + "";
  let month = lastDayOfMonth.getMonth() + 1 < 10
        ? "0" + (lastDayOfMonth.getMonth() + 1)
        : lastDayOfMonth.getMonth() + 1 + "";
  let date = lastDayOfMonth.getDate() + "";
  //文字列を作成
  return year + month + date;
}

async function getJsonlastMonths(last_time_id: string) {
  let retDocItem: any = null;
  let retRecords: any = {};
  let base_month: string = "";
  // 過去1カ月分取得する
  let n: number = 1;
  for (let i: number = 0; i < n; ) {
    try {
      if (last_time_id) {
        retDocItem = await getFirestoreDocData(
          "ts_check_records_phase2",
          last_time_id
        );
        if (retDocItem) {
          if (base_month === "") {
            base_month = getFirstDayOfMonth(
              retDocItem.attribution.approve_day,
              0
            );
          }
          let target_month = getFirstDayOfMonth(
            retDocItem.attribution.approve_day,
            0
          );
          let last_month = getFirstDayOfMonth(base_month, -1 * i);
          let complete_date = retDocItem.attribution.complete_date;
          last_time_id = retDocItem.attribution.last_time_id;
          if (complete_date && last_month !== target_month) {
            retRecords = retDocItem.records;
            i++;
          }
        } else {
          break;
        }
      } else {
        break;
      }
    } catch {
      break;
    }
  }
  return retRecords;
}

// テンプレート一覧取得
export const getTemplateData = async (conditions: string[]) => {
  let templateArray: TS_CHECK_ITEM_TEMPLATE[] = [];
  if (conditions.length === 0) {
    return templateArray;
  }
  const getTmpTempData = async (con: string[]) => {
    await getFirestoreDataWhereArrayAny(
      "tenpo_check_items_template",
      "attribution.conditions",
      con
    ).then((response: any) => {
      response.forEach((res: any) => {
        let data = res.data();
        let docID = data.doc_id;
        let skipFlg = false;
        for (let i = 0; i < templateArray.length; i++) {
          if (docID === templateArray[i].doc_id) {
            skipFlg = true;
          }
        }
        if (skipFlg) {
          return;
        }
        const templateValue: TS_CHECK_ITEM_TEMPLATE = {
          id: data.id,
          doc_id: data.doc_id,
          template_name: data.attribution.template_name,
          big_item_id: data.attribution.big_item_id,
          big_item_name: data.attribution.big_item_name,
          conditions: data.attribution.conditions,
          small_items: data.attribution.small_items,
          info: data.info,
        };
        templateArray.push(templateValue);
      });
      return templateArray;
    });
  };

  if (conditions && conditions.length > 10) {
    let workConditions = [];
    for (let i = 0; i < conditions.length; i++) {
      workConditions.push(conditions[i]);
      if (workConditions.length % 10 === 0 || i + 1 === conditions.length) {
        await getTmpTempData(workConditions);
        workConditions = [];
      }
    }
  } else {
    await getTmpTempData(conditions);
  }
  return templateArray;
};

// テンプレートのFirestoreデータ更新を行う
export const updateTSCheckTemplate = async (
  templateItem: TS_CHECK_ITEM_TEMPLATE,
  newFlg?: boolean
) => {
  const objects: any = {};
  objects["attribution"] = {
    big_item_id: templateItem.big_item_id,
    big_item_name: templateItem.big_item_name,
    template_name: templateItem.template_name,
    conditions: templateItem.conditions,
    small_items: templateItem.small_items,
  };
  objects["id"] = templateItem.id;
  objects["doc_id"] = templateItem.doc_id;
  objects["info"] = {
    create_date: newFlg
      ? firestore.FieldValue.serverTimestamp()
      : templateItem.info
      ? templateItem.info.create_date
      : null,
    create_user: newFlg
      ? getFirebaseUserInfo().uid
      : templateItem.info
      ? templateItem.info.create_user
      : "",
    update_date: firestore.FieldValue.serverTimestamp(),
    update_user: getFirebaseUserInfo().uid,
  };

  userLogging(
    "店舗衛生点検表",
    "新規作成#tenpo_check_items_templateテーブル更新",
    JSON.stringify(objects)
  );
  writeFirestoreDoc("tenpo_check_items_template", templateItem.doc_id, objects);
};

// テンプレートのFirestoreデータ削除を行う
export const deleteTsCheckTemplate = async (doc_id: string) => {
  deleteFirestoreDoc("tenpo_check_items_template", doc_id);
};

// ts_check_recordsのデータを取得
export const getTsCheckRecordsFirestoreData = async (tsCheckId: string) => {
  const data = await getFirestoreDocData("ts_check_records_phase2", tsCheckId).then(
    (response: any) => {
      return response;
    }
  );
  return data;
};

// Firestoreの生データでDEFAULT_MINUS_ITEMSの項目が"3"以外かチェック
export const checkDefaultMinusItemsInFirestore = async (tsCheckId: string): Promise<boolean> => {
  const data = await getTsCheckRecordsFirestoreData(tsCheckId);
  if (!data || !data.records) {
    return false;
  }

  // recordsの各項目をチェック
  for (const key in data.records) {
    const record = data.records[key];
    if (isDefaultMinusItem(record.big_item_id, record.small_item_id)) {
      // DEFAULT_MINUS_ITEMSの項目で"3"以外があれば変更されている
      if (record.button_value !== "3") {
        return true;
      }
    }
  }
  return false; // すべてデフォルト値("3")のまま
};
